import { getDeviceId } from "../local/device-identity";
import { getFirebaseServices } from "./firebase";

export const SYNC_V3_PROTOCOL_VERSION = 3;
export const SYNC_V3_MAX_MUTATIONS = 25;

const serverControlledFields = new Set([
  "recordRevision",
  "syncSequence",
  "serverUpdatedAt",
  "updatedAt",
  "lastUpdated",
  "updatedByDeviceId",
  "lastMutationId",
  "syncProtocolVersion",
  "deviceId",
]);

export type SyncV3EntityType = "case" | "workspaceRecord" | "config";
export type SyncV3Operation = "create" | "upsert" | "delete" | "restore" | "resolveConflict";
export type JsonRecord = Record<string, unknown>;

export interface SyncV3MutationInput {
  clientMutationId?: string;
  entityType: SyncV3EntityType;
  entityId: string;
  operation: SyncV3Operation;
  baseRevision: number;
  payload?: JsonRecord;
  explicitlyClearedPaths?: string[];
  conflictId?: string;
}

export interface SyncV3MutationResult {
  status: "applied" | "conflict" | "rejected";
  clientMutationId: string;
  entityType: SyncV3EntityType;
  entityId: string;
  recordRevision?: number;
  currentRevision?: number;
  syncSequence?: number;
  serverUpdatedAt?: string;
  conflictId?: string;
  message?: string;
  canonicalPayload?: JsonRecord | null;
}

export class SyncV3RequestError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = "SyncV3RequestError";
  }
}

function endpoint(): string {
  const configured = import.meta.env.VITE_SYNC_V3_MUTATIONS_ENDPOINT;
  return configured?.trim() || "/api/sync/mutations";
}

function mutationId(): string {
  return `studio-${crypto.randomUUID()}`;
}

function jsonValue(value: unknown): unknown {
  if (value === null || typeof value === "string" || typeof value === "boolean") return value;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error("Sync payload contains an invalid number.");
    return value;
  }
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(jsonValue);
  if (value && typeof value === "object") {
    const timestamp = value as { toDate?: () => unknown };
    if (timestamp.toDate) {
      const date: unknown = timestamp.toDate();
      if (date instanceof Date && !Number.isNaN(date.getTime())) return date.toISOString();
    }
    const result: JsonRecord = {};
    for (const [key, nested] of Object.entries(value as JsonRecord)) result[key] = jsonValue(nested);
    return result;
  }
  throw new Error("Sync payload contains an unsupported value.");
}

/** Remove only top-level metadata owned by the v3 service. */
export function mutationPayload(value: JsonRecord | undefined): JsonRecord {
  const source = value ?? {};
  const result: JsonRecord = {};
  for (const [key, nested] of Object.entries(source)) {
    if (!serverControlledFields.has(key)) result[key] = jsonValue(nested);
  }
  return result;
}

function stableValue(value: unknown): unknown {
  if (value === null || typeof value === "string" || typeof value === "boolean") return value;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error("Sync payload contains an invalid number.");
    return value;
  }
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === "object") {
    return Object.keys(value as JsonRecord).sort().reduce<JsonRecord>((result, key) => {
      result[key] = stableValue((value as JsonRecord)[key]);
      return result;
    }, {});
  }
  throw new Error("Sync payload contains an unsupported value.");
}

function stableStringify(value: unknown): string {
  return JSON.stringify(stableValue(value));
}

async function payloadChecksum(payload: JsonRecord): Promise<string> {
  const encoded = new TextEncoder().encode(stableStringify(payload));
  const digest = await crypto.subtle.digest("SHA-256", encoded);
  return `sha256:${Array.from(new Uint8Array(digest)).map((item) => item.toString(16).padStart(2, "0")).join("")}`;
}

async function request(uid: string, body: JsonRecord): Promise<unknown> {
  const currentUser = getFirebaseServices().auth.currentUser;
  if (!uid || currentUser?.uid !== uid) {
    throw new Error("The requested workspace does not belong to the signed-in account.");
  }
  const token = await currentUser.getIdToken();
  let response: Response;
  try {
    response = await fetch(endpoint(), {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (reason) {
    throw new SyncV3RequestError(
      reason instanceof Error ? `Studio could not reach the sync service: ${reason.message}` : "Studio could not reach the sync service.",
    );
  }
  const responseBody: unknown = await response.json().catch((): unknown => ({}));
  if (!response.ok) {
    const detail = responseBody && typeof responseBody === "object" && "error" in responseBody
      ? String((responseBody as { error?: unknown }).error)
      : `Sync service returned ${response.status}.`;
    throw new SyncV3RequestError(detail, response.status);
  }
  return responseBody;
}

/**
 * Submit a v3 mutation batch. The caller owns the returned mutation IDs, so a
 * retry of this function with the same prepared inputs is receipt-idempotent.
 */
export async function submitSyncV3Mutations(
  uid: string,
  inputs: readonly SyncV3MutationInput[],
): Promise<SyncV3MutationResult[]> {
  if (!inputs.length || inputs.length > SYNC_V3_MAX_MUTATIONS) {
    throw new Error(`Sync Protocol v3 accepts between 1 and ${SYNC_V3_MAX_MUTATIONS} mutations at a time.`);
  }
  const mutations = await Promise.all(inputs.map(async (input) => {
    const payload = mutationPayload(input.payload);
    return {
      clientMutationId: input.clientMutationId ?? mutationId(),
      entityType: input.entityType,
      entityId: input.entityId,
      operation: input.operation,
      baseRevision: input.baseRevision,
      payload,
      explicitlyClearedPaths: input.explicitlyClearedPaths ?? [],
      ...(input.conflictId ? { conflictId: input.conflictId } : {}),
      payloadChecksum: await payloadChecksum(payload),
    };
  }));
  const result = await request(uid, {
    protocolVersion: SYNC_V3_PROTOCOL_VERSION,
    deviceId: getDeviceId(),
    clientVersion: "logbookanaes-studio",
    mutations,
  });
  const results = result && typeof result === "object" && Array.isArray((result as { results?: unknown }).results)
    ? (result as { results: unknown[] }).results
    : null;
  if (!results || results.length !== mutations.length) {
    throw new SyncV3RequestError("The sync service returned an invalid mutation response.");
  }
  return results.map((item, index) => {
    if (!item || typeof item !== "object") throw new SyncV3RequestError("The sync service returned an invalid mutation result.");
    const value = item as SyncV3MutationResult;
    if (value.clientMutationId !== mutations[index]?.clientMutationId || !["applied", "conflict", "rejected"].includes(value.status)) {
      throw new SyncV3RequestError("The sync service returned a mismatched mutation result.");
    }
    return value;
  });
}

export function recordRevision(value: unknown): number {
  const revision = Number(value && typeof value === "object" ? (value as JsonRecord).recordRevision : 0);
  return Number.isInteger(revision) && revision > 0 ? revision : 0;
}
